import { PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store/hooks";

import type {
  Content,
  IHighlight,
  ScaledPosition,
} from "@argument-studio/react-pdf-highlighter-with-categories";
import {
  AreaHighlight,
  Highlight,
  PdfHighlighter,
  PdfLoader,
  Popup,
} from "@argument-studio/react-pdf-highlighter-with-categories";
import { saveAddHighlight, saveUpdateHighlight } from "@store/thunks/workspaceThunks";
import {
  selectViewedHighlights,
  selectCurrentPdfUrl,
  selectCategories,
  selectCurrentReading,
  selectWorkspaceId,
  selectAssessmentDiagnostics,
} from "@store/selectors";
import { fetchAndCachePdf } from "../utils/pdfCache";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Tip from "./Tip";
import HighlightPopup from "./HighlightPopup";
import DiagnosticBadge from "./DiagnosticBadge";
import { v4 } from "uuid";

interface Props {
  isSidebarOpen: boolean;
  scale: string;
  destinationPage: number;
  getPageCount: (pageCount: number) => void;
  getCurrentPage: (page: number) => void;
  sidebarWidth: number;
  canvasOn: boolean;
}

const parseIdFromHash = (): string =>
  document.location.hash.slice("#highlight-".length);

const resetHash = () => {
  document.location.hash = "";
};

const PdfViewer = ({
  scale,
  isSidebarOpen,
  destinationPage,
  getPageCount,
  getCurrentPage,
  sidebarWidth,
  children,
  canvasOn,
}: PropsWithChildren<Props>) => {
  const pdfHighlighterRef = useRef<PdfHighlighter<IHighlight>>(null);
  const highlights = useAppSelector(selectViewedHighlights);
  const url = useAppSelector(selectCurrentPdfUrl);
  const reading = useAppSelector(selectCurrentReading);
  const workspaceId = useAppSelector(selectWorkspaceId);
  const categories = useAppSelector(selectCategories);
  const diagnostics = useAppSelector(selectAssessmentDiagnostics);
  const dispatch = useAppDispatch();

  // Stage 4: merge in ghost expert highlights so PdfHighlighter renders
  // outlines where the student missed (or only partially matched) the
  // expert's selections. Ghosts get a "ghost:" id prefix so the
  // highlightTransform below can distinguish them; the wrapped CSS
  // class drives the dashed/translucent treatment.
  const GHOST_ID_PREFIX = "ghost:";
  const highlightsWithGhosts = useMemo(() => {
    if (!diagnostics.active || diagnostics.ghosts.length === 0) return highlights;
    const ghostItems = diagnostics.ghosts.map((g) => ({
      ...g,
      id: GHOST_ID_PREFIX + g.id,
    }));
    return [...highlights, ...ghostItems];
  }, [highlights, diagnostics.active, diagnostics.ghosts]);

  const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
  const [loadedDocumentId, setLoadedDocumentId] = useState<string | null>(null);

  // Fetch and cache PDF when document ID changes
  useEffect(() => {
    if (!url || !reading || !workspaceId) {
      setPdfData(null);
      setLoadedDocumentId(null);
      return;
    }

    // Skip if we already have this document loaded
    if (loadedDocumentId === reading.document_id && pdfData) {
      return;
    }

    let cancelled = false;

    const loadPdf = async () => {
      try {
        const blob = await fetchAndCachePdf(
          reading.document_id,
          workspaceId,
          url
        );
        if (cancelled) return;

        const arrayBuffer = await blob.arrayBuffer();
        const pdfDataArray = new Uint8Array(arrayBuffer);

        setPdfData(pdfDataArray);
        setLoadedDocumentId(reading.document_id);
      } catch (error) {
        console.error("Failed to load PDF:", error);
        if (!cancelled) {
          setPdfData(null);
          setLoadedDocumentId(null);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
    };
    // Only re-run when document ID changes - not when URL refreshes or workspace changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reading?.document_id]);

  /* pdfHighlighterRef.current is the child component. .scrollTo is its method.
  The removeEventlistener is to prevent many event listeners from piling up.
  The dependency array tells that a new listener should be attached
  when the state.highlights array is changed. */
  useEffect(() => {
    const scrollToHighlightFromHash = () => {
      const highlight = highlights.find(
        (highlight) => highlight.id === parseIdFromHash()
      );

      const highlighter = pdfHighlighterRef.current;

      if (highlight && highlighter) {
        highlighter.scrollTo(highlight);
      }
    };

    window.addEventListener("hashchange", scrollToHighlightFromHash);
    return () =>
      window.removeEventListener("hashchange", scrollToHighlightFromHash);
  }, [highlights]);

  function addNewHighlight(highlight: IHighlight) {
    dispatch(saveAddHighlight(highlight));
  }

  function modifyHighlight(
    id: string,
    position: Partial<ScaledPosition>,
    content: Partial<Content>
  ) {
    dispatch(saveUpdateHighlight({ id, position, content }));
  }

  if (!pdfData) {
    return <div className={canvasOn ? "pdf-viewer-container--hidden" : ""} />;
  }

  return (
    <div className={canvasOn ? "pdf-viewer-container--hidden" : ""}>
      <PdfLoader
        data={pdfData}
        beforeLoad={
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              marginLeft: isSidebarOpen ? `${sidebarWidth / 2}px` : "",
            }}
          >
            <CircularProgress color="success" size={100} />
          </Box>
        }
      >
        {(pdfDocument) => (
          <PdfHighlighter
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              height: "94vh",
              marginTop: "6vh",
              width: isSidebarOpen ? `calc(100vw - ${sidebarWidth}px)` : "",
              marginLeft: isSidebarOpen ? `${sidebarWidth}px` : "",
            }}
            categoryLabels={categories}
            pdfDocument={pdfDocument}
            enableAreaSelection={(event) => event.altKey}
            onScrollChange={resetHash}
            pdfScaleValue={scale}
            scrollRef={() => {
              // scrollRef callback - currently unused but required by PdfHighlighter
            }}
            ref={pdfHighlighterRef}
            onSelectionFinished={(
              position,
              content,
              hideTipAndSelection,
              transformSelection,
              clabels
            ) => (
              <Tip
                onOpen={transformSelection}
                onConfirm={(comment) => {
                  addNewHighlight({
                    id: v4(),
                    content,
                    position,
                    comment,
                  });

                  hideTipAndSelection();
                }}
                categoryLabels={clabels}
              />
            )}
            highlightTransform={(
              highlight,
              index,
              setTip,
              hideTip,
              viewportToScaled,
              screenshot,
              isScrolledTo
            ) => {
              // The lib prepends an internal `ghostHighlight` to the
              // highlights array while the user is dragging to select
              // (see node_modules .../PdfHighlighter.js: `allHighlights
              // = [...highlights, ghostHighlight]`). That preview shape
              // is `{ position }` — no id, no comment, no content. Our
              // code (and downstream <Highlight>/<Popup>) assumed
              // those fields exist; any single throwing
              // highlightTransform call blanks the whole PDF via the
              // PdfHighlighter error boundary. Render the lib's
              // transient preview with a bare Highlight (no diagnostic
              // wrapping, no popup) and return early.
              if (!highlight.id) {
                return (
                  <Highlight
                    key={`tip-preview-${index}`}
                    isScrolledTo={false}
                    position={highlight.position}
                    comment={{ text: "", category: "" }}
                    categoryLabels={categories}
                  />
                );
              }

              // Stage 4: ghost expert highlights are flagged by an id
              // prefix. Render them as a class-wrapped Highlight with
              // no popup interactions (they're visual-only).
              const isGhost = highlight.id.startsWith(GHOST_ID_PREFIX);
              const status = isGhost
                ? "ghost"
                : diagnostics.statusById[highlight.id];
              const diagnosticClass = status
                ? `assessment-diagnostic assessment-diagnostic--${status}`
                : "";

              const isTextHighlight = !Boolean(
                highlight.content && highlight.content.image
              );

              const component = isTextHighlight ? (
                <Highlight
                  isScrolledTo={isScrolledTo}
                  position={highlight.position}
                  comment={highlight.comment}
                  categoryLabels={categories}
                />
              ) : (
                <AreaHighlight
                  categoryLabels={categories}
                  isScrolledTo={isScrolledTo}
                  highlight={highlight}
                  comment={highlight.comment}
                  onChange={(boundingRect) => {
                    modifyHighlight(
                      highlight.id,
                      { boundingRect: viewportToScaled(boundingRect) },
                      { image: screenshot(boundingRect) }
                    );
                  }}
                />
              );

              // Ghost overlays: no popup wrapping (read-only, visual cue
              // only) — just the diagnostic-classed wrapper.
              if (isGhost) {
                return (
                  <div key={index} className={diagnosticClass}>
                    {component}
                  </div>
                );
              }

              const diagnosticMessages =
                status && status !== "matched"
                  ? diagnostics.messagesById[highlight.id]
                  : undefined;

              return (
                <div key={index} className={diagnosticClass}>
                  <Popup
                    popupContent={
                      <HighlightPopup selectedHighlight={highlight} />
                    }
                    onMouseOver={(popupContent) =>
                      setTip(highlight, () => popupContent)
                    }
                    onMouseOut={() => {
                      hideTip();
                    }}
                    children={component}
                  />
                  {diagnosticMessages && diagnosticMessages.length > 0 && (
                    <DiagnosticBadge
                      messages={diagnosticMessages}
                      rect={highlight.position.boundingRect}
                    />
                  )}
                </div>
              );
            }}
            highlights={highlightsWithGhosts}
            getPageCount={getPageCount}
            getCurrentPage={getCurrentPage}
            destinationPage={destinationPage}
          />
        )}
      </PdfLoader>
      {children}
    </div>
  );
};

export default PdfViewer;
